// :)
/**
 * Atomically merge Synkro hook entries into ~/.cursor/hooks.json.
 *
 * Cursor hooks reuse the same TypeScript scripts as Claude Code (cc-*.ts),
 * run with SYNKRO_HOOK_FORMAT=cursor so _synkro-common translates CC JSON output
 * to Cursor's permission JSON for preToolUse. CC install is unchanged.
 */
import { readFileSync, writeFileSync, renameSync, mkdirSync } from 'node:fs';
import { dirname, resolve, normalize } from 'node:path';
import { homedir } from 'node:os';

export interface CursorHookConfig {
  /** Cursor-specific bash/shell judge (preToolUse with Shell|Bash matcher) */
  bashJudgeScriptPath: string;
  /** Cursor-specific post-edit capture (afterFileEdit event shape) */
  editCaptureScriptPath: string;
  /** Capture afterAgentThought/afterAgentResponse before transcript redaction */
  agentCaptureScriptPath: string;
  /** Shared cc-*.ts scripts (invoked with SYNKRO_HOOK_FORMAT=cursor) */
  bashFollowupScriptPath: string;
  editPrecheckScriptPath: string;
  cwePrecheckScriptPath: string;
  cvePrecheckScriptPath: string;
  planJudgeScriptPath: string;
  agentJudgeScriptPath: string;
  stopSummaryScriptPath: string;
  sessionStartScriptPath: string;
  userPromptSubmitScriptPath: string;
  transcriptSyncScriptPath: string;
  installScanScriptPath: string;
  /** MCP server governance (beforeMCPExecution): allow/blocklist + grade calls */
  mcpGateScriptPath: string;
}

const SYNKRO_MARKER = '__synkro_managed__';

function shellQuote(s: string): string {
  return "'" + s.replace(/'/g, "'\\''") + "'";
}

/** Run a shared cc-*.ts hook with Cursor output translation. */
function cursorCcCmd(scriptPath: string): string {
  return 'env SYNKRO_HOOK_FORMAT=cursor bun run ' + shellQuote(scriptPath);
}

function bunRunCmd(scriptPath: string): string {
  return 'bun run ' + shellQuote(scriptPath);
}

const ALLOWED_PARENT_DIRS = [
  resolve(homedir(), '.cursor'),
  resolve(homedir(), '.config', 'cursor'),
];

function validateHooksPath(path: string): string {
  const resolved = resolve(normalize(path));
  if (!ALLOWED_PARENT_DIRS.some(dir => resolved.startsWith(dir + '/') || resolved === dir)) {
    throw new Error(`Hooks path must be under ~/.cursor or ~/.config/cursor, got: ${resolved}`);
  }
  return resolved;
}

interface CursorHookEntry {
  command: string;
  timeout?: number;
  failClosed?: boolean;
  matcher?: string;
  [SYNKRO_MARKER]?: boolean;
}

interface CursorHooksFile {
  version?: number;
  hooks?: {
    [event: string]: CursorHookEntry[];
  };
}

function readHooksFile(rawPath: string): CursorHooksFile {
  const safePath = validateHooksPath(rawPath);
  try {
    const raw = readFileSync(safePath, 'utf-8');
    return JSON.parse(raw) as CursorHooksFile;
  } catch (err: any) {
    if (err?.code === 'ENOENT') return { version: 1, hooks: {} };
    throw new Error(`Failed to parse ${safePath}: ${(err as Error).message}`);
  }
}

function writeHooksFileAtomic(rawPath: string, data: CursorHooksFile): void {
  const safePath = validateHooksPath(rawPath);
  mkdirSync(dirname(safePath), { recursive: true });
  const tmpPath = `${safePath}.synkro.tmp`;
  writeFileSync(tmpPath, JSON.stringify(data, null, 2) + '\n', { encoding: 'utf-8', mode: 0o600 });
  renameSync(tmpPath, safePath);
}

function isSynkroEntry(entry: any): boolean {
  if (entry?.[SYNKRO_MARKER]) return true;
  return typeof entry?.command === 'string' && entry.command.includes('/.synkro/hooks/');
}

const ALL_EVENTS = [
  'sessionStart', 'sessionEnd', 'beforeSubmitPrompt', 'stop',
  'beforeShellExecution', 'afterShellExecution',
  'preToolUse', 'afterFileEdit', 'postToolUse',
  'beforeMCPExecution',
  'afterAgentThought', 'afterAgentResponse',
];

function removeSynkroEntries(hooks: CursorHooksFile['hooks'], event: string): void {
  if (!hooks) return;
  const arr = hooks[event];
  if (!Array.isArray(arr)) return;
  hooks[event] = arr.filter((entry: any) => !isSynkroEntry(entry));
}

function pushCcHook(
  hooks: CursorHooksFile['hooks'],
  event: string,
  scriptPath: string,
  opts: { timeout: number; matcher?: string; failClosed?: boolean },
): void {
  hooks![event] = hooks![event] ?? [];
  hooks![event]!.push({
    command: cursorCcCmd(scriptPath),
    timeout: opts.timeout,
    failClosed: opts.failClosed ?? false,
    ...(opts.matcher ? { matcher: opts.matcher } : {}),
    [SYNKRO_MARKER]: true,
  });
}

export function installCursorHooks(hooksJsonPath: string, config: CursorHookConfig): void {
  const file = readHooksFile(hooksJsonPath);
  file.version = file.version ?? 1;
  file.hooks = file.hooks ?? {};

  for (const evt of ALL_EVENTS) {
    removeSynkroEntries(file.hooks, evt);
  }

  const h = file.hooks;

  pushCcHook(h, 'sessionStart', config.sessionStartScriptPath, { timeout: 5 });
  pushCcHook(h, 'sessionEnd', config.stopSummaryScriptPath, { timeout: 10 });
  pushCcHook(h, 'beforeSubmitPrompt', config.userPromptSubmitScriptPath, { timeout: 5 });
  pushCcHook(h, 'stop', config.transcriptSyncScriptPath, { timeout: 3 });

  // beforeShellExecution: install scan runs first (caches result for bash judge)
  h.beforeShellExecution = h.beforeShellExecution ?? [];
  h.beforeShellExecution.push({
    command: cursorCcCmd(config.installScanScriptPath),
    timeout: 8,
    failClosed: false,
    [SYNKRO_MARKER]: true,
  });

  h.beforeShellExecution.push({
    command: cursorCcCmd(config.cwePrecheckScriptPath),
    timeout: 30,
    failClosed: false,
    [SYNKRO_MARKER]: true,
  });

  h.beforeShellExecution.push({
    command: bunRunCmd(config.bashJudgeScriptPath),
    timeout: 30,
    failClosed: false,
    [SYNKRO_MARKER]: true,
  });

  pushCcHook(h, 'afterShellExecution', config.bashFollowupScriptPath, { timeout: 10 });

  // preToolUse: install scan before bash judge (caches result for bash judge)
  h.preToolUse = h.preToolUse ?? [];
  h.preToolUse.push({
    command: cursorCcCmd(config.installScanScriptPath),
    timeout: 8,
    failClosed: false,
    matcher: 'Shell|Bash|terminal|run_terminal_cmd|execute_command',
    [SYNKRO_MARKER]: true,
  });

  h.preToolUse.push({
    command: cursorCcCmd(config.cwePrecheckScriptPath),
    timeout: 30,
    failClosed: false,
    matcher: 'Shell|Bash|terminal|run_terminal_cmd|execute_command',
    [SYNKRO_MARKER]: true,
  });

  h.preToolUse.push({
    command: bunRunCmd(config.bashJudgeScriptPath),
    timeout: 30,
    failClosed: false,
    matcher: 'Shell|Bash|Read|ReadFile|Grep|Glob|terminal|run_terminal_cmd|execute_command|read_file|grep_search|file_search|list_dir|codebase_search|delete_file',
    [SYNKRO_MARKER]: true,
  });

  pushCcHook(h, 'preToolUse', config.editPrecheckScriptPath, {
    timeout: 30,
    matcher: 'Write|Edit|StrReplace|MultiEdit|NotebookEdit|edit_file|reapply|edit_notebook|ApplyPatch|apply_patch',
  });
  pushCcHook(h, 'preToolUse', config.cwePrecheckScriptPath, {
    timeout: 30,
    matcher: 'Write|Edit|StrReplace|MultiEdit|NotebookEdit|edit_file|reapply|edit_notebook|ApplyPatch|apply_patch',
  });
  pushCcHook(h, 'preToolUse', config.cvePrecheckScriptPath, {
    timeout: 10,
    matcher: 'Write|Edit|StrReplace|MultiEdit|NotebookEdit|edit_file|reapply|edit_notebook|ApplyPatch|apply_patch',
  });
  pushCcHook(h, 'preToolUse', config.agentJudgeScriptPath, {
    timeout: 30,
    matcher: 'Agent|Task',
  });
  pushCcHook(h, 'preToolUse', config.planJudgeScriptPath, {
    timeout: 45,
    matcher: 'ExitPlanMode|SwitchMode|CreatePlan',
  });

  h.afterFileEdit = h.afterFileEdit ?? [];
  h.afterFileEdit.push({
    command: bunRunCmd(config.editCaptureScriptPath),
    timeout: 15,
    failClosed: false,
    [SYNKRO_MARKER]: true,
  });

  pushCcHook(h, 'postToolUse', config.bashFollowupScriptPath, {
    timeout: 10,
    matcher: 'Shell|Bash|terminal|run_terminal_cmd|execute_command|delete_file',
  });

  // beforeMCPExecution: MCP server governance — fires once per MCP tool call.
  // Same shared cc-mcp-gate.ts as Claude Code (allow/blocklist + grade allowed
  // calls + emit connect/tool_call/blocked events). Blocking: a blocklisted /
  // un-allowlisted server is denied. No matcher — this event is MCP-only.
  pushCcHook(h, 'beforeMCPExecution', config.mcpGateScriptPath, { timeout: 30 });

  h.afterAgentThought = h.afterAgentThought ?? [];
  h.afterAgentThought.push({
    command: bunRunCmd(config.agentCaptureScriptPath),
    timeout: 5,
    failClosed: false,
    [SYNKRO_MARKER]: true,
  });
  h.afterAgentResponse = h.afterAgentResponse ?? [];
  h.afterAgentResponse.push({
    command: bunRunCmd(config.agentCaptureScriptPath),
    timeout: 5,
    failClosed: false,
    [SYNKRO_MARKER]: true,
  });

  writeHooksFileAtomic(hooksJsonPath, file);
}

export function uninstallCursorHooks(hooksJsonPath: string): boolean {
  let file: CursorHooksFile;
  try {
    file = readHooksFile(hooksJsonPath);
  } catch {
    return false;
  }
  if (!file.hooks) return false;

  for (const evt of ALL_EVENTS) {
    removeSynkroEntries(file.hooks, evt);
  }

  for (const evt of ALL_EVENTS) {
    if (Array.isArray(file.hooks[evt]) && file.hooks[evt].length === 0) {
      delete file.hooks[evt];
    }
  }
  if (Object.keys(file.hooks).length === 0) {
    delete file.hooks;
  }

  writeHooksFileAtomic(hooksJsonPath, file);
  return true;
}

function preToolUseUsesScript(hooks: CursorHookEntry[] | undefined, scriptBasename: string): boolean {
  return (hooks ?? []).some((e) =>
    isSynkroEntry(e) && typeof e.command === 'string' && e.command.includes(scriptBasename),
  );
}

export function inspectCursorHooks(hooksJsonPath: string): {
  installed: boolean;
  sessionStart: boolean;
  sessionEnd: boolean;
  beforeSubmitPrompt: boolean;
  stop: boolean;
  beforeShellExecution: boolean;
  afterShellExecution: boolean;
  preToolUse: boolean;
  preToolUseBash: boolean;
  preToolUseEdit: boolean;
  preToolUseCwe: boolean;
  preToolUseCve: boolean;
  preToolUseAgent: boolean;
  preToolUsePlan: boolean;
  afterFileEdit: boolean;
  postToolUse: boolean;
  beforeMCPExecution: boolean;
} {
  let file: CursorHooksFile;
  try {
    file = readHooksFile(hooksJsonPath);
  } catch {
    return {
      installed: false,
      sessionStart: false, sessionEnd: false, beforeSubmitPrompt: false, stop: false,
      beforeShellExecution: false, afterShellExecution: false,
      preToolUse: false, preToolUseBash: false, preToolUseEdit: false,
      preToolUseCwe: false, preToolUseCve: false, preToolUseAgent: false, preToolUsePlan: false,
      afterFileEdit: false, postToolUse: false, beforeMCPExecution: false,
    };
  }
  const h = file.hooks ?? {};
  const sessionStart = (h.sessionStart ?? []).some((e) => isSynkroEntry(e));
  const sessionEnd = (h.sessionEnd ?? []).some((e) => isSynkroEntry(e));
  const beforeSubmitPrompt = (h.beforeSubmitPrompt ?? []).some((e) => isSynkroEntry(e));
  const stop = (h.stop ?? []).some((e) => isSynkroEntry(e));
  const beforeShellExecution = (h.beforeShellExecution ?? []).some((e) => isSynkroEntry(e));
  const afterShellExecution = (h.afterShellExecution ?? []).some((e) => isSynkroEntry(e));
  const pre = h.preToolUse ?? [];
  const preToolUseBash = preToolUseUsesScript(pre, 'cc-bash-judge') || preToolUseUsesScript(pre, 'cursor-bash-judge');
  const preToolUseEdit = preToolUseUsesScript(pre, 'cc-edit-precheck') || preToolUseUsesScript(pre, 'cursor-edit-precheck');
  const preToolUseCwe = preToolUseUsesScript(pre, 'cc-cwe-precheck');
  const preToolUseCve = preToolUseUsesScript(pre, 'cc-cve-precheck');
  const preToolUseAgent = preToolUseUsesScript(pre, 'cc-agent-judge');
  const preToolUsePlan = preToolUseUsesScript(pre, 'cc-plan-judge');
  const preToolUse = preToolUseBash || preToolUseEdit || preToolUseCwe || preToolUseCve || preToolUseAgent || preToolUsePlan;
  const afterFileEdit = (h.afterFileEdit ?? []).some((e) => isSynkroEntry(e));
  const postToolUse = (h.postToolUse ?? []).some((e) => isSynkroEntry(e));
  const beforeMCPExecution = (h.beforeMCPExecution ?? []).some((e) => isSynkroEntry(e));
  return {
    installed: sessionStart || sessionEnd || beforeSubmitPrompt || stop
      || beforeShellExecution || afterShellExecution || preToolUse || afterFileEdit || postToolUse
      || beforeMCPExecution,
    sessionStart, sessionEnd, beforeSubmitPrompt, stop,
    beforeShellExecution, afterShellExecution,
    preToolUse, preToolUseBash, preToolUseEdit, preToolUseCwe, preToolUseCve, preToolUseAgent, preToolUsePlan,
    afterFileEdit, postToolUse, beforeMCPExecution,
  };
}
